import React, { Component } from 'react';
import PropTypes from 'prop-types';
import track from 'react-tracking';
import BEMHelper from '@ynap/bem-helper';
import { trackPage, trackRetry } from '../analytics';
import { Button } from '@ynap/button';
import { raygun } from '@ynap/clientside-monitoring';

const bem = new BEMHelper('ErrorPage');

function timeout(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

@track()
export default class RetryErrorPage extends Component {
    static propTypes = {
        backoff: PropTypes.number,
        className: PropTypes.string,
        maxRetries: PropTypes.number,
        action: PropTypes.func.isRequired,
        messages: PropTypes.shape({
            retry: PropTypes.shape({
                title: PropTypes.func.isRequired,
                retrynow: PropTypes.func.isRequired,
                timetoretry: PropTypes.func.isRequired,
            }).isRequired,
        }).isRequired,
        analytics: PropTypes.object,
        error: PropTypes.shape({
            status: PropTypes.string,
            message: PropTypes.string,
        }),
        tracking: PropTypes.shape({
            trackEvent: PropTypes.func.isRequired,
        }).isRequired,
        displayMessageError: PropTypes.string,
        country: PropTypes.string,
        language: PropTypes.string,
        deviceType: PropTypes.string,
    };

    static defaultProps = {
        maxRetries: 3,
        analytics: {},
        backoff: 1,
        error: null,
        country: 'GB',
        language: 'en',
        deviceType: 'website',
    };

    constructor(props) {
        super(props);

        this.state = {
            retries: 0,
            timer: props.backoff,
            retrying: false,
        };
    }

    @track(trackPage)
    componentDidMount() {
        const { error, displayMessageError } = this.props;
        if (error) {
            const url = raygun.helpers.getUnlocalisedUrl(window?.location?.pathname);
            raygun.helpers.agent('send', {
                error: new Error(`retry error page: ${displayMessageError || url}`),
                tags: ['retry-error-page', `${error?.status}`, `${error?.message}`, url],
                customData: {
                    ...error,
                    prevPath: document.referrer,
                },
            });
        }
        this._mount = true;
        this._counting = true;
        this.callAction();
    }

    componentWillUnmount() {
        this._mount = false;
    }

    resetTimer = () => {
        this.setState({
            timer: this.props.backoff * Math.pow(2, this.state.retries),
        });
    };

    countDownTimeout = async () => {
        while (this.state.timer > 0) {
            await timeout(1000);
            this.setState({
                timer: this.state.timer - 1,
            });
        }
        return;
    };

    retrynow = () => {
        const { action, maxRetries, tracking } = this.props;
        this._counting = false;
        if (this.state.retries !== maxRetries || this.state.timer !== 0) {
            this.setState({
                retries: maxRetries,
                timer: 0,
                retrying: true,
            });
        }
        action();
        tracking.trackEvent(trackRetry());
    };

    callAction = async () => {
        const { action, maxRetries } = this.props;

        if (this.state.retries < maxRetries && this._mount) {
            try {
                this.resetTimer();

                await this.countDownTimeout();
                this.setState({
                    retrying: true,
                });
                if (this._counting) {
                    return await Promise.all([action(), await timeout(1000)].map((p) => p.catch(() => 'error'))).then((results) => {
                        this.setState({
                            retrying: false,
                        });
                        return results.map((result) => {
                            if (result === 'error') {
                                throw 'error';
                            }
                        });
                    });
                }
            } catch (error) {
                if (this._mount) {
                    this.setState({
                        retries: this.state.retries + 1,
                        retrying: false,
                    });

                    return this.callAction();
                }
            }
        }
    };

    render() {
        const { className, messages, maxRetries } = this.props;
        return (
            <div className={bem(null, null, className)}>
                <div className={bem('container')}>
                    <h3 className={bem('title')}>{messages.retry.title()}</h3>
                    {this.state.retries < maxRetries ? (
                        <p className={bem('details')}>
                            {this.state.retrying ? messages.retry.retryingnow() : messages.retry.timetoretry({ seconds: this.state.timer })}
                        </p>
                    ) : null}

                    <Button type="secondary" className={bem('retry')} onClick={this.retrynow}>
                        {messages.retry.retrynow()}
                    </Button>
                </div>
            </div>
        );
    }
}
